/** The interactive Comps tool — v1 js/comps.js as a full-screen overlay:
 * filters rail · Leaflet map (OSM + Esri satellite, price-label pins, subject
 * star) · results rail with star/weight · summary bar (weighted $/sqft →
 * projected ARV → "save to lead"). Same backend endpoint, same state contract,
 * same math — see comps/api.ts. Leaflet loads lazily from cdnjs exactly like
 * v1 (cmpLoadLeaflet); if the CDN is unreachable the tool still works, the
 * map pane just says so. */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { moneyExact } from "../../lib/money";
import type { Lead } from "../../types";
import { idbGet, idbSet } from "../../lib/idb";
import { natKey } from "../../lib/natkey";
import {
  COMPS_MSG, autoPrefillFilters, compKey, compsSummary, narrowCompsTool,
  type CompRow, type CompsFilters, type CompsSubject, type SavedComp,
} from "./api";

// ---- minimal Leaflet surface (loaded from cdnjs at runtime) ------------------

interface LMap {
  remove(): void;
  invalidateSize(): void;
  fitBounds(b: [number, number][], o?: Record<string, unknown>): void;
  removeLayer(m: LMarker): void;
}
interface LMarker {
  addTo(m: LMap): LMarker;
  bindPopup(html: string): LMarker;
  on(ev: string, fn: () => void): LMarker;
}
interface LLayer {
  addTo(m: LMap): LLayer;
}
interface LeafletNS {
  map(el: HTMLElement): LMap;
  tileLayer(url: string, opts: Record<string, unknown>): LLayer;
  marker(ll: [number, number], opts: Record<string, unknown>): LMarker;
  divIcon(opts: Record<string, unknown>): unknown;
  control: { layers(base: Record<string, LLayer>): LLayer };
}

declare global {
  interface Window {
    L?: LeafletNS;
    _crm2LeafletP?: Promise<void>;
  }
}

/** v1 cmpLoadLeaflet, verbatim URLs. */
function loadLeaflet(): Promise<void> {
  if (window.L) return Promise.resolve();
  if (window._crm2LeafletP) return window._crm2LeafletP;
  window._crm2LeafletP = new Promise((res, rej) => {
    const css = document.createElement("link");
    css.rel = "stylesheet";
    css.href = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css";
    document.head.appendChild(css);
    const js = document.createElement("script");
    js.src = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js";
    js.onload = () => res();
    js.onerror = rej;
    document.head.appendChild(js);
  });
  return window._crm2LeafletP;
}

const usd = (n: number): string => moneyExact(n);

const statusHue = (s: string): string => (s === "sold" ? "cmp-sold" : s === "pending" ? "cmp-pending" : "cmp-active");

export function CompsPanel({
  lead,
  fields,
  onClose,
  onSearch,
  onPhotos,
  onPersist,
  onApplyArv,
  inline = false,
}: {
  lead: Lead;
  fields: Record<string, unknown> | undefined;
  onClose: () => void;
  onSearch: (address: string, f: CompsFilters, onWake: () => void) => Promise<{ comps: CompRow[]; subject: CompsSubject | null }>;
  onPhotos: (comp: CompRow) => Promise<string[]>;
  onPersist: (address: string, state: { filters: CompsFilters; saved: Record<string, SavedComp> }) => void;
  onApplyArv: (address: string, arv: number) => void;
  /** Render inside the Comps tab (no fixed overlay, no header/close, Esc does
   * not dismiss) instead of as a full-screen modal. v1 shows comps inline. */
  inline?: boolean;
}) {
  const k = natKey(lead.address);
  const init = useRef(narrowCompsTool(fields?.["compsTool"]));
  const [filters, setFilters] = useState<CompsFilters>(() =>
    autoPrefillFilters(init.current.filters, lead, fields),
  );
  const [saved, setSaved] = useState<Record<string, SavedComp>>(init.current.saved);
  const [results, setResults] = useState<CompRow[]>(init.current.results);
  const [subject, setSubject] = useState<CompsSubject | null>(init.current.subject);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [modalIx, setModalIx] = useState<number | null>(null);
  const [photoIx, setPhotoIx] = useState(0);
  const [mapDead, setMapDead] = useState(false);
  const [fitSeq, setFitSeq] = useState(0); // bump = fit bounds on next draw

  // results cache: this device remembers the last search per lead (v1 keeps
  // them in the org blob; v2 keeps the blob lean — IndexedDB instead)
  useEffect(() => {
    if (init.current.results.length) return;
    let alive = true;
    void idbGet<{ results: CompRow[]; subject: CompsSubject | null }>("comps_results_v1:" + k).then((c) => {
      if (!alive || !c || !Array.isArray(c.results) || !c.results.length) return;
      setResults((prev) => (prev.length ? prev : c.results));
      setSubject((prev) => prev ?? c.subject);
      setFitSeq((n) => n + 1);
    });
    return () => {
      alive = false;
    };
  }, [k]);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // modal first, then the overlay — v1's layering. Inline (Comps tab) never
      // closes the drawer on Esc; only its own photo modal responds.
      setModalIx((m) => {
        if (m !== null) return null;
        if (!inline) onClose();
        return m;
      });
    };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose, inline]);

  const summary = useMemo(() => compsSummary({ saved, results }, lead.sqft), [saved, results, lead.sqft]);

  const persist = useCallback(
    (f: CompsFilters, s: Record<string, SavedComp>) => {
      onPersist(lead.address, { filters: f, saved: s });
    },
    [lead.address, onPersist],
  );

  const setF = (key: keyof CompsFilters, v: string) => setFilters((p) => ({ ...p, [key]: v }));

  const togglePill = (key: "sold" | "active" | "pending" | "srcZ" | "srcR" | "srcH") => {
    setFilters((p) => {
      const cur = key.startsWith("src") ? p[key] !== false : !!p[key];
      const next = { ...p, [key]: !cur };
      persist(next, saved);
      return next;
    });
  };

  const search = async () => {
    if (busy) return;
    const stats = (["sold", "active", "pending"] as const).filter((x) => filters[x]);
    if (!stats.length) {
      setNote(COMPS_MSG.needStatus);
      return;
    }
    setBusy(true);
    setNote(COMPS_MSG.searching);
    try {
      const r = await onSearch(lead.address, filters, () => setNote(COMPS_MSG.waking));
      setResults(r.comps);
      setSubject(r.subject);
      setFitSeq((n) => n + 1);
      setNote(COMPS_MSG.found(r.comps.length));
      persist(filters, saved);
      void idbSet("comps_results_v1:" + k, { at: Date.now(), results: r.comps, subject: r.subject });
    } catch (e) {
      setNote(e instanceof Error && e.message ? e.message : COMPS_MSG.unreachable);
    } finally {
      setBusy(false);
    }
  };

  const toggleSave = (c: CompRow) => {
    const key = compKey(c);
    setSaved((p) => {
      const next = { ...p };
      if (next[key]) delete next[key];
      else next[key] = { weight: 1, ...c };
      persist(filters, next);
      return next;
    });
  };

  const setWeight = (key: string, w: number) => {
    setSaved((p) => {
      const cur = p[key];
      if (!cur) return p;
      const next = { ...p, [key]: { ...cur, weight: w || 1 } };
      persist(filters, next);
      return next;
    });
  };

  const applyArv = () => {
    if (summary.arv === null) return;
    onApplyArv(lead.address, summary.arv);
    setNote(COMPS_MSG.arvSaved);
  };

  // photos lazy-load (v1 cmpLoadPhotos: once per comp, only when <2 photos)
  const photosTried = useRef(new Set<string>());
  const modalComp = modalIx !== null ? results[modalIx] ?? null : null;
  useEffect(() => {
    const c = modalComp;
    if (!c) return;
    const key = compKey(c);
    if ((c.photos?.length ?? 0) >= 2 || photosTried.current.has(key)) return;
    photosTried.current.add(key);
    let alive = true;
    void onPhotos(c)
      .then((ph) => {
        if (!alive || ph.length < 2) return;
        setResults((prev) => prev.map((r) => (compKey(r) === key ? { ...r, photos: ph } : r)));
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [modalComp, onPhotos]);

  // ---- the map ---------------------------------------------------------------
  const mapEl = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<LMap | null>(null);
  const pinsRef = useRef<LMarker[]>([]);
  const lastFit = useRef(0);

  useEffect(() => {
    let alive = true;
    loadLeaflet()
      .then(() => {
        if (!alive || !window.L || !mapEl.current) return;
        if (!mapRef.current) {
          const L = window.L;
          const map = L.map(mapEl.current);
          const osm = L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19, attribution: "© OpenStreetMap" });
          const sat = L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", { maxZoom: 19, attribution: "Esri" });
          sat.addTo(map);
          L.control.layers({ Satellite: sat, Map: osm }).addTo(map);
          mapRef.current = map;
        }
        drawPins();
      })
      .catch(() => {
        if (alive) setMapDead(true);
      });
    return () => {
      alive = false;
      if (mapRef.current) {
        try {
          mapRef.current.remove();
        } catch {
          /* leaflet teardown is best-effort */
        }
        mapRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const drawPins = useCallback(() => {
    const L = window.L;
    const map = mapRef.current;
    if (!L || !map) return;
    for (const m of pinsRef.current) {
      try {
        map.removeLayer(m);
      } catch {
        /* pin already gone */
      }
    }
    pinsRef.current = [];
    const pts: [number, number][] = [];
    if (subject?.lat !== undefined && subject.lat !== null && subject.lng !== undefined) {
      const m = L.marker([subject.lat, subject.lng], {
        icon: L.divIcon({
          className: "",
          html: '<div class="cmp-pin-subject">★</div>',
          iconSize: [30, 30],
          iconAnchor: [15, 15],
        }),
      })
        .addTo(map)
        .bindPopup("<b>Subject</b><br>" + (subject.address ?? ""));
      pinsRef.current.push(m);
      pts.push([subject.lat, subject.lng]);
    }
    results.forEach((c, i) => {
      if (c.lat === null || c.lng === null) return;
      const isSaved = !!saved[compKey(c)];
      const col = c.status === "sold" ? (isSaved ? "#059669" : "#dc2626") : c.status === "pending" ? "#d97706" : "#2563eb";
      const m = L.marker([c.lat, c.lng], {
        icon: L.divIcon({
          className: "",
          html: `<div class="cmp-pin" style="background:${col}">$${Math.round((c.price || 0) / 1000)}K</div>`,
          iconSize: [50, 20],
          iconAnchor: [25, 10],
        }),
      })
        .addTo(map)
        .on("click", () => {
          setModalIx(i);
          setPhotoIx(0);
        });
      pinsRef.current.push(m);
      pts.push([c.lat, c.lng]);
    });
    window.setTimeout(() => {
      map.invalidateSize();
      if (pts.length && fitSeq !== lastFit.current) {
        lastFit.current = fitSeq;
        map.fitBounds(pts, { padding: [30, 30] });
      }
    }, 80);
  }, [results, saved, subject, fitSeq]);

  useEffect(() => {
    drawPins();
  }, [drawPins]);

  // ---- render ----------------------------------------------------------------
  const modalPhotos = modalComp ? (modalComp.photos?.length ? modalComp.photos : modalComp.photo ? [modalComp.photo] : []) : [];
  const modalSaved = modalComp ? saved[compKey(modalComp)] : undefined;

  return (
    <div className={inline ? "cmpinline" : "cmpoverlay"} role={inline ? undefined : "dialog"} aria-label="Comps tool">
      {inline ? null : (
        <header className="cmphead">
          <div>
            <h2>Comps — {lead.address}</h2>
            <span className="sub">
              {lead.beds ?? "?"}bd {lead.baths ?? "?"}ba {lead.sqft ? lead.sqft.toLocaleString() + " sqft" : "sqft unknown"}
              {lead.yearBuilt ? " · built " + lead.yearBuilt : ""} · Zillow / Realtor / Homes via the backend
            </span>
          </div>
          <button className="close" onClick={onClose} title="Close (Esc)">×</button>
        </header>
      )}

      <div className="cmpsummary">
        <div className="cmptile">
          <span className="lbl">Props comped</span>
          <strong>{summary.count}</strong>
          {summary.usingAll ? <em>all results — star to refine</em> : null}
        </div>
        <div className="cmptile">
          <span className="lbl">Avg $/sqft</span>
          <strong>{summary.avgPpsf !== null ? usd(summary.avgPpsf) : "—"}</strong>
        </div>
        <div className="cmptile">
          <span className="lbl">Avg CDOM</span>
          <strong>{summary.avgCdom !== null ? summary.avgCdom : "—"}</strong>
        </div>
        <div className="cmptile cmparv">
          <span className="lbl">Projected ARV</span>
          <strong>{summary.arv !== null ? usd(summary.arv) : "—"}</strong>
          {summary.arv !== null ? (
            <button className="dlink" onClick={applyArv}>save to lead</button>
          ) : lead.sqft ? null : (
            <em>need subject sqft</em>
          )}
        </div>
      </div>

      <div className="cmpgrid">
        <aside className="cmpfilters">
          <div className="cmpfhead">Search filters</div>
          <label className="lbl">Radius (miles)</label>
          <input value={String(filters.radius)} onChange={(e) => setF("radius", e.target.value)} />
          <label className="lbl">Days back (sold)</label>
          <input value={String(filters.days)} onChange={(e) => setF("days", e.target.value)} />
          <div className="cmpfrow">
            <div>
              <label className="lbl">Price min</label>
              <input placeholder="any" value={filters.priceMin ?? ""} onChange={(e) => setF("priceMin", e.target.value)} />
            </div>
            <div>
              <label className="lbl">Price max</label>
              <input placeholder="any" value={filters.priceMax ?? ""} onChange={(e) => setF("priceMax", e.target.value)} />
            </div>
          </div>
          <div className="cmpfrow">
            <div>
              <label className="lbl">Beds (min)</label>
              <input placeholder="any" value={String(filters.beds)} onChange={(e) => setF("beds", e.target.value)} />
            </div>
            <div>
              <label className="lbl">Baths (min)</label>
              <input placeholder="any" value={String(filters.baths)} onChange={(e) => setF("baths", e.target.value)} />
            </div>
          </div>
          <div className="cmpfrow">
            <div>
              <label className="lbl">Sqft min</label>
              <input placeholder="any" value={String(filters.sqftMin)} onChange={(e) => setF("sqftMin", e.target.value)} />
            </div>
            <div>
              <label className="lbl">Sqft max</label>
              <input placeholder="any" value={String(filters.sqftMax)} onChange={(e) => setF("sqftMax", e.target.value)} />
            </div>
          </div>
          <div className="cmpfrow">
            <div>
              <label className="lbl">Year min</label>
              <input placeholder="any" value={String(filters.yearMin)} onChange={(e) => setF("yearMin", e.target.value)} />
            </div>
            <div>
              <label className="lbl">Year max</label>
              <input placeholder="any" value={String(filters.yearMax)} onChange={(e) => setF("yearMax", e.target.value)} />
            </div>
          </div>
          <label className="lbl">Sources — click to include/exclude</label>
          <div className="cmppills">
            {([["srcZ", "Zillow"], ["srcR", "Realtor"], ["srcH", "Homes"]] as const).map(([key, label]) => (
              <button
                key={key}
                className={"pillbtn" + (filters[key] !== false ? " on-src" : " off")}
                onClick={() => togglePill(key)}
              >
                {label}
              </button>
            ))}
          </div>
          <label className="lbl">Status — click to include/exclude</label>
          <div className="cmppills">
            {([["sold", "Sold", "on-sold"], ["active", "Active", "on-active"], ["pending", "Pending", "on-pending"]] as const).map(([key, label, hue]) => (
              <button key={key} className={"pillbtn" + (filters[key] ? " " + hue : " off")} onClick={() => togglePill(key)}>
                {label}
              </button>
            ))}
          </div>
          <button className="btn primary cmpgo" disabled={busy} onClick={() => void search()}>
            {busy ? "Searching… (15-40s)" : "Search Comps"}
          </button>
          <div className="sub cmpcredits">~1-2 Scrapfly credits per search</div>
          {note ? <div className="cmpnote">{note}</div> : null}
        </aside>

        <div className="cmpmapwrap">
          <div ref={mapEl} className="cmpmap" />
          {mapDead ? <div className="cmpmapdead">Map couldn't load (CDN blocked) — the list and summary still work.</div> : null}
        </div>

        <aside className="cmprail">
          {results.length === 0 ? (
            <div className="cmpemptyrail">No results yet — set your filters and hit Search Comps.</div>
          ) : (
            results.map((c, i) => {
              const key = compKey(c);
              const sv = saved[key];
              return (
                <div key={key + i} className={"cmpcard" + (sv ? " issaved" : "")} onClick={() => { setModalIx(i); setPhotoIx(0); }}>
                  <div className="cmpcardrow">
                    {c.photo ? <img src={c.photo} loading="lazy" alt="" onError={(e) => ((e.target as HTMLImageElement).style.display = "none")} /> : null}
                    <div className="cmpcardbody">
                      <div className="cmpcardtop">
                        <span className="cmpaddr">
                          {c.link ? (
                            <a href={c.link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                              {(c.address || "").split(",")[0]}
                            </a>
                          ) : (
                            (c.address || "").split(",")[0]
                          )}
                        </span>
                        <button
                          className={"cmpstar" + (sv ? " on" : "")}
                          title={sv ? "Unsave" : "Save comp"}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSave(c);
                          }}
                        >
                          {sv ? "★" : "☆"}
                        </button>
                      </div>
                      <div className="cmpprice">
                        ${(c.price || 0).toLocaleString("en-US")} <span className="ppsf">${c.ppsf}/sf</span>{" "}
                        <span className={"cmppill " + statusHue(c.status)}>{c.status}</span>
                      </div>
                      <div className="cmpmeta">
                        {c.beds ?? "—"}/{c.baths ?? "—"} · {(c.sqft || 0).toLocaleString("en-US")} sf
                        {c.year ? " · " + c.year : ""}
                        {c.dist_mi !== null ? " · " + c.dist_mi + "mi" : ""}
                        {c.cdom !== null ? " · CDOM " + Math.round(c.cdom) : ""}
                        {c.sold_date ? " · " + String(c.sold_date).slice(0, 10) : ""} ·{" "}
                        <span className="cmpsrc">{c.source}</span>
                      </div>
                      {sv ? (
                        <select
                          value={String(sv.weight ?? 1)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => setWeight(key, parseFloat(e.target.value) || 1)}
                        >
                          <option value="0.5">Low weight</option>
                          <option value="1">Medium</option>
                          <option value="2">High weight</option>
                        </select>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </aside>
      </div>

      {modalComp ? (
        <div className="cmpmodalscrim" onClick={() => setModalIx(null)}>
          <div className="cmpmodal" onClick={(e) => e.stopPropagation()}>
            {modalPhotos.length ? (
              <div className="cmpmodalimg">
                <img src={modalPhotos[Math.min(photoIx, modalPhotos.length - 1)]} alt="" onError={(e) => ((e.target as HTMLImageElement).style.display = "none")} />
                {modalPhotos.length > 1 ? (
                  <>
                    <button className="cmpnav prev" onClick={() => setPhotoIx((p) => (p - 1 + modalPhotos.length) % modalPhotos.length)}>‹</button>
                    <button className="cmpnav next" onClick={() => setPhotoIx((p) => (p + 1) % modalPhotos.length)}>›</button>
                    <span className="cmpimgix">{Math.min(photoIx, modalPhotos.length - 1) + 1} / {modalPhotos.length}</span>
                  </>
                ) : null}
              </div>
            ) : null}
            <div className="cmpmodalbody">
              <div className="cmpmodalhead">
                <div>
                  <h3>{modalComp.address}</h3>
                  <span className="sub">
                    <span className={"cmppill " + statusHue(modalComp.status)}>{modalComp.status}</span> ·{" "}
                    <span className="cmpsrc">{modalComp.source}</span>
                    {modalComp.dist_mi !== null ? " · " + modalComp.dist_mi + " mi away" : ""}
                  </span>
                </div>
                <button className="close" onClick={() => setModalIx(null)}>×</button>
              </div>
              <div className="cmpstats">
                {(
                  [
                    ["Price", usd(modalComp.price)],
                    ["$/sqft", usd(modalComp.ppsf)],
                    ["Sqft", (modalComp.sqft || 0).toLocaleString("en-US")],
                    ["Beds/Baths", (modalComp.beds ?? "—") + "/" + (modalComp.baths ?? "—")],
                    ["Year built", modalComp.year ?? "—"],
                    ["Sold", modalComp.sold_date ? String(modalComp.sold_date).slice(0, 10) : "—"],
                    ["CDOM", modalComp.cdom !== null ? Math.round(modalComp.cdom) : "—"],
                    ["Distance", modalComp.dist_mi !== null ? modalComp.dist_mi + " mi" : "—"],
                  ] as const
                ).map(([l, v]) => (
                  <div key={l} className="cmpstat">
                    <span className="lbl">{l}</span>
                    <strong>{v}</strong>
                  </div>
                ))}
              </div>
              <div className="cmpmodalacts">
                <button className={"btn" + (modalSaved ? "" : " primary")} onClick={() => { toggleSave(modalComp); setModalIx(null); }}>
                  {modalSaved ? "★ Unsave comp" : "☆ Save as comp"}
                </button>
                {modalSaved ? (
                  <select value={String(modalSaved.weight ?? 1)} onChange={(e) => setWeight(compKey(modalComp), parseFloat(e.target.value) || 1)}>
                    <option value="0.5">Low weight</option>
                    <option value="1">Medium</option>
                    <option value="2">High weight</option>
                  </select>
                ) : null}
                {modalComp.link ? (
                  <a className="btn ghost cmpout" href={modalComp.link} target="_blank" rel="noreferrer">
                    Open on {modalComp.source} ↗
                  </a>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
